import React from 'react'
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import { useCompany } from '@/contexts/CompanyContext'
import { useNavigationBadges } from '@/hooks/useNavigationBadges'
import { CompanySwitcher } from '@/components/CompanySwitcher'
import { DemoBanner } from '@/components/DemoBanner'
import { Button } from '@/components/ui/Button'
import {
  Users,
  Briefcase,
  MapPin,
  MessageSquare,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  X,
  BarChart3,
  ClipboardSignature,
  ChevronLeft,
  ChevronRight,
  FileText,
  FileCheck,
  TrendingUp
} from 'lucide-react'

const defaultNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: BarChart3, id: 'dashboard' },
  { name: 'Customers', href: '/customers', icon: Users, id: 'customers' },
  { name: 'Jobs', href: '/jobs', icon: Briefcase, id: 'jobs' },
  { name: 'Invoices', href: '/invoices', icon: FileText, id: 'invoices' },
  { name: 'Quotes', href: '/quotes', icon: FileCheck, id: 'quotes' },
  { name: 'Routing', href: '/routing', icon: MapPin, id: 'routing' },
  { name: 'Messages', href: '/messages', icon: MessageSquare, id: 'messages' },
  { name: 'Reports', href: '/reports', icon: TrendingUp, id: 'reports' },
  { name: 'Settings', href: '/settings', icon: SettingsIcon, id: 'settings' }
]

const dataNavigation = [
  { name: 'Existing Customer Updates', href: '/data/existing-updates', icon: ClipboardSignature }
]

export const MainLayout: React.FC = () => {
  const { user, signOut, isSuperAdmin } = useAuth()
  const { preferences } = useUserPreferences()
  const { currentCompany } = useCompany()
  const { data: badges } = useNavigationBadges()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const [signingOut, setSigningOut] = React.useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false)

  // Redirect cancelled/expired subscriptions to billing page (unless super admin)
  const isExpiredOrCancelled = currentCompany && (currentCompany.subscription_status === 'cancelled' || currentCompany.subscription_status === 'expired')
  const isOnSettingsPage = location.pathname.startsWith('/settings')

  if (isExpiredOrCancelled && !isOnSettingsPage && !isSuperAdmin) {
    return <Navigate to="/settings" replace />
  }

  // Use company logo if available, otherwise fall back to Sergio logo
  const logoUrl = (currentCompany as any)?.logo_url || '/brand/sergio-logo.png'
  const companyName = currentCompany?.name || 'Sergio'

  const isRouteActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }

    return location.pathname === href || location.pathname.startsWith(`${href}/`)
  }

  // Get ordered navigation based on user preferences
  const getOrderedNavigation = () => {
    if (!preferences?.navigation_order) {
      return defaultNavigation
    }

    const navigationMap = new Map(defaultNavigation.map(item => [item.id, item]))

    // Get items in user's saved order
    const orderedItems = preferences.navigation_order
      .map(id => navigationMap.get(id))
      .filter(Boolean) as typeof defaultNavigation

    // Find any new items not in the saved order and append them
    const orderedIds = new Set(orderedItems.map(item => item.id))
    const newItems = defaultNavigation.filter(item => !orderedIds.has(item.id))

    return [...orderedItems, ...newItems]
  }

  const navigation = getOrderedNavigation()

  const handleSignOut = async () => {
    if (signingOut) return // Prevent multiple clicks

    setSigningOut(true)
    // signOut now handles the redirect internally
    await signOut()
  }

  // Get badge info for a navigation item
  const getBadgeInfo = (itemId: string) => {
    if (!badges) return null

    const badgeMap: Record<string, typeof badges.messages | null> = {
      messages: badges.messages,
      quotes: badges.quotes,
      jobs: badges.jobs,
      invoices: badges.invoices
    }

    const badge = badgeMap[itemId]
    if (!badge || badge.count === 0) return null

    return badge
  }

  const renderNav = (isMobile = false) => (
    <nav className="flex-1 space-y-4">
      <div className="space-y-1">
        {navigation.map((item) => {
          const isActive = isRouteActive(item.href)
          const badgeInfo = getBadgeInfo(item.id)

          return (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => isMobile && setMobileMenuOpen(false)}
              className={[
                'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 relative',
                isActive
                  ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)]',
                sidebarCollapsed && !isMobile && 'justify-center'
              ].join(' ')}
              title={sidebarCollapsed && !isMobile ? item.name : undefined}
            >
              <item.icon
                className={`h-5 w-5 flex-shrink-0 transition-colors ${isActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-subtle)]'}`}
              />
              {(!sidebarCollapsed || isMobile) && (
                <>
                  <span className="flex-1">{item.name}</span>
                  {badgeInfo && (
                    <span
                      className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full ${
                        badgeInfo.variant === 'urgent'
                          ? 'bg-red-100 dark:bg-red-950/30 text-red-700 dark:text-red-400'
                          : 'bg-blue-100 dark:bg-blue-950/30 text-[#0088DD] dark:text-blue-400'
                      }`}
                    >
                      {badgeInfo.count}
                    </span>
                  )}
                </>
              )}
              {sidebarCollapsed && !isMobile && badgeInfo && (
                <span
                  className={`absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center text-[10px] font-bold rounded-full ${
                    badgeInfo.variant === 'urgent'
                      ? 'bg-red-500 text-white'
                      : 'bg-[#0088DD] text-white'
                  }`}
                >
                  {badgeInfo.count > 9 ? '9+' : badgeInfo.count}
                </span>
              )}
            </Link>
          )
        })}
      </div>

      {(!sidebarCollapsed || isMobile) && (
      <div className="space-y-2">
        <p className="px-3 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-subtle)]">
          Analytics
        </p>
        <div className="space-y-1">
          {dataNavigation.map((item) => {
            const isActive = isRouteActive(item.href)
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => isMobile && setMobileMenuOpen(false)}
                className={[
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
                  isActive
                    ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)]'
                ].join(' ')}
              >
                <item.icon
                  className={`h-5 w-5 flex-shrink-0 transition-colors ${isActive ? 'text-[var(--color-primary)]' : 'text-[var(--color-text-subtle)]'}`}
                />
                {item.name}
              </Link>
            )
          })}
        </div>
      </div>
      )}
    </nav>
  )

  const userInitial = user?.email?.[0]?.toUpperCase() ?? 'S'

  return (
    <div className="min-h-screen bg-[var(--color-surface-muted)] text-[var(--color-text)]">
      {/* Desktop Sidebar */}
      <div className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'
      }`}>
        <div className="flex flex-col flex-grow pt-6 pb-6 bg-[var(--color-surface-elevated)] border-r border-[var(--color-border)] shadow-soft relative">
          {/* Collapse Button */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-6 z-10 h-6 w-6 rounded-full bg-[var(--color-surface-elevated)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-muted)] hover:text-[var(--color-text-strong)] hover:bg-[var(--color-surface-hover)] transition-colors shadow-sm"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>

          <div className="px-6">
          {preferences?.show_brand_logo !== false && !sidebarCollapsed && (
            <div className="flex items-center justify-center">
              <Link to="/dashboard" className="transition-opacity hover:opacity-90">
                <div className="inline-flex flex-col items-center gap-3 text-center">
                  <img
                    src={logoUrl}
                    alt={companyName}
                    className={`rounded-full shadow-card border-2 border-[var(--color-border)] object-cover bg-white ${
                      preferences?.brand_logo_size === 'small' ? 'h-10 w-10' :
                      preferences?.brand_logo_size === 'large' ? 'h-16 w-16' :
                      'h-12 w-12'
                    }`}
                  />
                  <div className="space-y-1">
                    <span className="block font-display text-xl font-bold tracking-tight text-strong">
                      {companyName}
                    </span>
                    {!currentCompany && (
                      <span className="block text-xs font-medium text-muted">
                        Field Service Management
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          )}

          {preferences?.show_brand_logo !== false && sidebarCollapsed && (
            <div className="flex items-center justify-center">
              <Link to="/dashboard" className="transition-opacity hover:opacity-90">
                <img
                  src={logoUrl}
                  alt={companyName}
                  className="h-10 w-10 rounded-full shadow-card border-2 border-[var(--color-border)] object-cover bg-white"
                />
              </Link>
            </div>
          )}

          {/* Company Switcher */}
          {!sidebarCollapsed && (
            <div className="mt-6 mb-4">
              <CompanySwitcher />
            </div>
          )}

          <div className={`${preferences?.show_brand_logo !== false && !sidebarCollapsed ? 'mt-6' : sidebarCollapsed ? 'mt-10' : 'mt-6'} flex-grow flex flex-col`}>
            {renderNav()}
          </div>

          </div>

          <div className="border-t border-[var(--color-border)] pt-5 mt-6 px-6">
            <div className={`flex items-center p-3 rounded-lg bg-[var(--color-surface)] transition-colors ${
              sidebarCollapsed ? 'justify-center flex-col gap-2' : 'justify-between'
            }`}>
              {sidebarCollapsed ? (
                <>
                  <div className="h-9 w-9 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center font-semibold text-sm">
                    {userInitial}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSignOut}
                    disabled={signingOut}
                    loading={signingOut}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                    title="Sign out"
                  >
                    {!signingOut && <LogOut className="h-4 w-4" />}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {userInitial}
                    </div>
                    <div className="text-sm leading-tight min-w-0">
                      <p className="font-medium text-strong truncate">{user?.email}</p>
                      <p className="text-xs text-subtle">Logged in</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSignOut}
                    disabled={signingOut}
                    loading={signingOut}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] flex-shrink-0 -mr-2"
                  >
                    {!signingOut && <LogOut className="h-4 w-4" />}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-30 bg-[var(--color-surface)] border-b border-subtle shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/dashboard" className="flex items-center gap-3">
            {preferences?.show_brand_logo !== false && (
              <img
                src={logoUrl}
                alt={companyName}
                className={`rounded-full shadow-card border-2 border-[var(--color-border)] object-cover bg-white ${
                  preferences?.brand_logo_size === 'large' ? 'h-12 w-12' : 'h-10 w-10'
                }`}
              />
            )}
            <div className="leading-tight">
              <span className="block text-base font-bold text-strong">{companyName}</span>
              {!currentCompany && (
                <span className="block text-xs font-medium text-muted">Field Service Management</span>
              )}
            </div>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-[var(--color-text-muted)]"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm">
          <div className="absolute inset-y-0 left-0 w-72 bg-[var(--color-surface-elevated)] border-r border-[var(--color-border)] shadow-floating p-6 flex flex-col">
            <div className="flex items-center justify-between">
              {preferences?.show_brand_logo !== false && (
                <div className="inline-flex items-center gap-3">
                  <img
                    src={logoUrl}
                    alt={companyName}
                    className={`rounded-full shadow-card border-2 border-[var(--color-border)] object-cover bg-white ${
                      preferences?.brand_logo_size === 'large' ? 'h-12 w-12' : 'h-10 w-10'
                    }`}
                  />
                  <div className="space-y-0">
                    <span className="block font-display text-lg font-bold tracking-tight text-strong">
                      {companyName}
                    </span>
                    {!currentCompany && (
                      <span className="block text-xs font-medium text-muted">
                        Field Service Management
                      </span>
                    )}
                  </div>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(false)}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>

            {/* Company Switcher - Mobile */}
            <div className="mt-6">
              <CompanySwitcher />
            </div>

            <div className="mt-6 flex-1 flex flex-col">
              {renderNav(true)}
            </div>
            <div className="border-t border-[var(--color-border)] pt-4 mt-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--color-surface)] transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary)] flex items-center justify-center font-semibold text-sm flex-shrink-0">
                    {userInitial}
                  </div>
                  <p className="text-sm text-strong font-medium truncate">{user?.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  disabled={signingOut}
                  loading={signingOut}
                  className="text-[var(--color-text-muted)] hover:text-[var(--color-danger)] flex-shrink-0 -mr-2"
                >
                  {!signingOut && <LogOut className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`flex flex-col flex-1 transition-all duration-300 ${
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'
      }`}>
        <DemoBanner />
        <main className="flex-1">
          <div className="py-8">
            <div className="mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
              <Outlet />
            </div>
          </div>
        </main>

        <footer className="bg-[var(--color-surface-muted)] py-4 px-4 sm:px-6 lg:px-8 border-t border-subtle">
          <div className="mx-auto text-center text-xs text-muted">
            <Link to="/privacy" className="transition-colors hover:text-strong px-2">Privacy Policy</Link>
            <span className="mx-1 border-l border-subtle" />
            <Link to="/terms" className="transition-colors hover:text-strong px-2">Terms of Service</Link>
          </div>
        </footer>
      </div>
    </div>
  )
}
